// src/stores/useGroupStore.js
import { defineStore } from "pinia";
import * as groupService from "@/services/groupService";

export const useGroupStore = defineStore("group", {
  state: () => ({
    groups: [],
    selectedGroup: null, // 🔥 Centraliza o objeto rico (detalhado) para o Modal
    selectedGroupIds: [],
    idGroup: null, // 🔹 espelho de idAuthor
    error: null,
    totalGroupsCount: 0,
    totalGroupsPages: 1,
    currentGroupsPage: 1,
    groupsPageSize: 10,
    groupSearchTerm: "",
    shouldForceGroupsPageOne: false,
  }),

  getters: {
    isAllGroupsSelected: (state) => (total) =>
      state.selectedGroupIds.length === total,
  },

  actions: {
    // ===== Identificador =====
    setIdGroup(id) {
      this.idGroup = id;
    },

    // ===== Seleção e Limpeza =====
    setSelectedGroup(group) {
      this.selectedGroup = group;
    },

    // 🔥 NOVO: Limpa o grupo detalhado (chame ao fechar modais)
    clearSelectedGroup() {
      this.selectedGroup = null;
    },

    toggleGroupSelection(id) {
      const i = this.selectedGroupIds.indexOf(id);
      if (i > -1) this.selectedGroupIds.splice(i, 1);
      else this.selectedGroupIds.push(id);
    },

    selectAllGroups(ids) {
      this.selectedGroupIds = [...ids];
    },

    clearGroupSelection() {
      this.selectedGroupIds = [];
    },

    // ===== Listagem NÃO paginada =====
    async fetchGroups() {
      try {
        const response = await groupService.fetchGroups();
        this.groups = response.data;
      } catch (error) {
        this.error = error.response?.data || error.message;
        console.error("❌ Erro ao buscar grupos:", this.error);
        throw new Error(this.error);
      }
    },

    // ===== Listagem paginada =====
    async fetchGroupsPaged(pageNumber = 1, pageSize = 10, search = "") {
      try {
        const response = await groupService.fetchGroupsPaged(
          pageNumber,
          pageSize,
          search
        );
        this.groups = response.data.data;
        this.totalGroupsCount = response.data.totalCount;
        this.totalGroupsPages = response.data.totalPages;
        this.currentGroupsPage = pageNumber;
        this.groupSearchTerm = search;
      } catch (error) {
        const mensagem = error.response?.data?.message || error.message;
        this.error = mensagem;
        console.error("❌ Erro ao buscar grupos paginados:", mensagem);
        throw new Error(mensagem);
      }
    },

    async getGroupById(id) {
      try {
        const response = await groupService.getGroupById(id);

        // 🔥 CRÍTICO: Persiste no estado reativo para o Modal exibir fotos/nomes
        this.selectedGroup = response.data;

        // Sincroniza a lista local com os dados mais recentes
        const idx = this.groups.findIndex((g) => g.groupId === id);
        if (idx === -1) {
          this.groups.push(this.selectedGroup);
        } else {
          this.groups[idx] = { ...this.groups[idx], ...this.selectedGroup };
        }

        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || error.message;
        console.error("❌ Erro ao obter grupo:", this.error);
        throw new Error(this.error);
      }
    },

    // ===== Create =====
    async createGroup(data) {
      try {
        const response = await groupService.createGroup(data);
        this.shouldForceGroupsPageOne = true;
        await this.fetchGroupsPaged(
          1,
          this.groupsPageSize,
          this.groupSearchTerm
        );
        return response.data;
      } catch (error) {
        console.error("❌ Erro ao cadastrar grupo:", error);
        throw error;
      }
    },

    // ===== Update =====
    async updateGroup(data) {
      try {
        await groupService.updateGroup(data);

        const id = data.groupId;
        if (id != null) {
          // Atualiza na lista geral
          const idx = this.groups.findIndex((g) => g.groupId === id);
          if (idx !== -1) {
            this.groups[idx] = { ...this.groups[idx], ...data };
          }
          // 🔥 Sincroniza o selectedGroup se for o grupo que está sendo editado
          if (this.selectedGroup && this.selectedGroup.groupId === id) {
            this.selectedGroup = { ...this.selectedGroup, ...data };
          }
        }
      } catch (error) {
        console.error("❌ Erro ao atualizar grupo:", error);
        throw error;
      }
    },

    // ===== Delete único =====
    async deleteGroupById(id) {
      try {
        await groupService.deleteGroup(id);

        await this.fetchGroupsPaged(
          this.currentGroupsPage,
          this.groupsPageSize,
          this.groupSearchTerm
        );

        if (this.groups.length === 0 && this.currentGroupsPage > 1) {
          this.currentGroupsPage -= 1;
          await this.fetchGroupsPaged(
            this.currentGroupsPage,
            this.groupsPageSize,
            this.groupSearchTerm
          );
        }
      } catch (error) {
        const serverMessage = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir grupo:", serverMessage);

        // Lógica de tratamento de erros de FK (mantida)
        if (
          serverMessage.includes(
            "association between entities 'Group' and 'UserGroup'"
          )
        ) {
          throw new Error(
            "Não é possível excluir o grupo com usuários associados."
          );
        } else if (serverMessage.includes("fk_group_activity_permission")) {
          throw new Error("Grupo possui vínculos com atividades.");
        } else if (serverMessage.includes("fk_group_text_permission")) {
          throw new Error("Grupo possui vínculos com textos.");
        } else if (serverMessage.includes("fk_group_note_permission")) {
          throw new Error("Grupo possui vínculos com notas.");
        }

        throw new Error(serverMessage);
      }
    },

    // ===== Delete em lote =====
    async deleteSelectedGroups() {
      if (!this.selectedGroupIds.length) return;

      try {
        await groupService.deleteGroupsBatch(this.selectedGroupIds);
        this.clearGroupSelection();

        await this.fetchGroupsPaged(
          this.currentGroupsPage,
          this.groupsPageSize,
          this.groupSearchTerm
        );

        if (this.groups.length === 0 && this.currentGroupsPage > 1) {
          this.currentGroupsPage -= 1;
          await this.fetchGroupsPaged(
            this.currentGroupsPage,
            this.groupsPageSize,
            this.groupSearchTerm
          );
        }
      } catch (error) {
        const serverMessage = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir grupos em lote:", serverMessage);
        throw new Error(serverMessage);
      }
    },

    // ===== Busca =====
    setGroupSearchTerm(term) {
      this.groupSearchTerm = term;
      this.currentGroupsPage = 1;
      this.shouldForceGroupsPageOne = true;
      this.fetchGroupsPaged(1, this.groupsPageSize, term);
    },

    // ===== Aliases de Simetria =====
    toggleSelection(id) {
      this.toggleGroupSelection(id);
    },
    selectAll(ids) {
      this.selectAllGroups(ids);
    },
    clearSelection() {
      this.clearGroupSelection();
    },
    setSearchTerm(term) {
      this.setGroupSearchTerm(term);
    },
  },
});
